<template>
  <div class="wf-apply">
    <el-card shadow="never" class="wf-apply-card">
      <div slot="header" class="wf-apply-head">发起申请</div>

      <!-- 选择流程 -->
      <div class="wf-picker">
        <!-- 已选中：折叠为一行 -->
        <div v-if="flowId" class="wf-picked">
          <span class="wf-picked__icon" :style="iconStyle(currentFlow)">
            <img v-if="currentFlow && currentFlow.iconUrl" :src="iconUrlOf(currentFlow)" class="wf-flow-ico-img" />
            <i v-else :class="iconOf(currentFlow)"></i>
          </span>
          <span class="wf-picked__name">{{ currentFlowName }}</span>
          <span v-if="currentFlowCategory" class="wf-picked__cat">{{ currentFlowCategory }}</span>
          <span v-if="currentFlowSubCategory" class="wf-picked__cat">{{ currentFlowSubCategory }}</span>
          <el-button type="text" size="small" icon="el-icon-refresh-left" @click="reselect">重新选择</el-button>
        </div>

        <!-- 未选中：搜索 + 分类 Tab + 卡片 -->
        <template v-else>
          <div class="wf-picker__search">
            <el-input v-model="keyword" size="small" clearable prefix-icon="el-icon-search"
                      placeholder="请输入审批单名称" />
          </div>

          <div class="wf-picker__tabs" v-if="flowGroups.length">
            <span v-for="g in flowGroups" :key="g.category" class="wf-tab"
                  :class="{ 'is-active': g.category === activeCategory }"
                  @click="activeCategory = g.category">{{ g.category }}</span>
          </div>

          <div class="wf-picker__cards" v-loading="flowLoading">
            <!-- 无二级分类：平铺在最前，不带分组小标题 -->
            <div v-if="currentSubGroups.ungrouped.length" class="wf-picker__grid">
              <div v-for="f in currentSubGroups.ungrouped" :key="f.id" class="wf-flow-card" @click="onFlowChange(f.id)">
                <span class="wf-flow-card__icon" :style="iconStyle(f)">
                  <img v-if="f.iconUrl" :src="iconUrlOf(f)" class="wf-flow-ico-img" />
                  <i v-else :class="iconOf(f)"></i>
                </span>
                <span class="wf-flow-card__name">{{ f.flowName }}</span>
              </div>
            </div>
            <!-- 有二级分类：按二级分类分段，每段带一个小标题 -->
            <div v-for="sg in currentSubGroups.groups" :key="sg.subCategory" class="wf-picker__group">
              <div class="wf-sub-title">{{ sg.subCategory }}</div>
              <div class="wf-picker__grid">
                <div v-for="f in sg.items" :key="f.id" class="wf-flow-card" @click="onFlowChange(f.id)">
                  <span class="wf-flow-card__icon" :style="iconStyle(f)">
                    <img v-if="f.iconUrl" :src="iconUrlOf(f)" class="wf-flow-ico-img" />
                    <i v-else :class="iconOf(f)"></i>
                  </span>
                  <span class="wf-flow-card__name">{{ f.flowName }}</span>
                </div>
              </div>
            </div>
            <div v-if="!currentFlowItems.length" class="wf-picker__empty">
              {{ flowLoading ? '加载中…' : '暂无匹配的流程' }}
            </div>
          </div>
        </template>
      </div>

      <!-- 申请标题 -->
      <el-form v-if="flowId" :label-width="titleLabelWidth" size="small">
        <el-form-item label="申请标题">
          <el-input v-model="title" placeholder="申请标题" style="width: 320px" maxlength="100" />
        </el-form-item>
      </el-form>

      <!-- 动态表单 -->
      <div v-if="flowId && formSchema" class="wf-apply-form">
        <el-divider content-position="left">填写申请内容</el-divider>
        <form-renderer
          ref="formRenderer"
          :schema="formSchema"
          v-model="formData"
          :user-options="userOptions"
          :dept-options="deptOptions"
          :chance-options="chanceOptions"
          :my-chance-options="myChanceOptions"
          :project-options="projectOptions"
          :my-project-options="myProjectOptions"
          :role-options="roleOptions"
          :upload-action="uploadAction"
          resizable
        />
      </div>

      <!-- 发起人自选审批人 -->
      <div v-if="selfNodes.length" class="wf-apply-self">
        <el-divider content-position="left">指定审批人</el-divider>
        <el-form :label-width="selfLabelWidth" size="small">
          <el-form-item v-for="n in selfNodes" :key="n.nodeKey" :label="(n.props && n.props.name) || '审批人'" required>
            <el-select v-model="selfAssignees[n.nodeKey]" multiple filterable placeholder="请选择审批人" style="width: 320px">
              <el-option v-for="u in userOptions" :key="u.id" :label="u.trueName || u.userName" :value="u.id" />
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <div class="wf-apply-footer" v-if="flowId">
        <el-button size="small" @click="resetAll">重置</el-button>
        <el-button size="small" type="primary" icon="el-icon-promotion" :loading="submitting" @click="submit">提交申请</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import FormRenderer from './components/FormRenderer.vue'
import { flatten } from './components/flowUtil'
import { pickIcon, fullIconUrl } from './components/flowIcon'
import { labelWidthOf } from './components/labelWidth'
import { publishedFlows, getFlow } from '@/api/wflow/flow'
import { getForm } from '@/api/wflow/form'
import { startProcess } from '@/api/wflow/process'
import { getUserList } from '@/api/plat/PlatStaff'
import { getDepartmentList } from '@/api/plat/DepMana'
import { getTRoleSelectByPage } from '@/api/plat/PosMana'
import { getSaleChanceOptions, getSaleProjectOptions } from '@/api/sale'

// 分类/流程名关键词 → 图标与配色：统一由 components/flowIcon.js 提供
const NO_CATEGORY = '未分类'

export default {
  name: 'WflowApply',
  components: { FormRenderer },
  data() {
    return {
      flows: [], flowId: null, flowKey: '', title: '',
      keyword: '', activeCategory: '', flowLoading: false,
      formSchema: null, formData: {},
      selfNodes: [], selfAssignees: {},
      userOptions: [], deptOptions: [], chanceOptions: [], myChanceOptions: [], projectOptions: [], myProjectOptions: [], roleOptions: [],
      // 附件上传地址：复用平台文件管理上传接口
      uploadAction: this.$store.state.IPAss.TP + '/work/file-manage/upload',
      submitting: false
    }
  },
  computed: {
    // 关键词过滤后的流程
    filteredFlows() {
      const kw = (this.keyword || '').trim().toLowerCase()
      if (!kw) return this.flows
      return this.flows.filter(f =>
        (f.flowName || '').toLowerCase().indexOf(kw) > -1 ||
        (f.category || '').toLowerCase().indexOf(kw) > -1 ||
        (f.subCategory || '').toLowerCase().indexOf(kw) > -1)
    },
    // 按分类分区（后端已按 category 排序，这里保持原有顺序）
    flowGroups() {
      const map = {}
      const order = []
      this.filteredFlows.forEach(f => {
        const c = f.category || NO_CATEGORY
        if (!map[c]) { map[c] = []; order.push(c) }
        map[c].push(f)
      })
      return order.map(c => ({ category: c, items: map[c] }))
    },
    // 当前分类下的卡片
    currentFlowItems() {
      const g = this.flowGroups.find(x => x.category === this.activeCategory)
      return g ? g.items : []
    },
    // 当前分类内再按二级分类分段：无二级分类的平铺在最前(不带小标题)，其余按出现顺序聚合
    currentSubGroups() {
      const ungrouped = []
      const map = {}
      const order = []
      this.currentFlowItems.forEach(f => {
        const s = (f.subCategory || '').trim()
        if (!s) { ungrouped.push(f); return }
        if (!map[s]) { map[s] = []; order.push(s) }
        map[s].push(f)
      })
      return { ungrouped, groups: order.map(s => ({ subCategory: s, items: map[s] })) }
    },
    currentFlow() {
      return this.flows.find(f => f.id === this.flowId) || null
    },
    currentFlowName() {
      return this.currentFlow ? this.currentFlow.flowName : ''
    },
    currentFlowCategory() {
      return this.currentFlow ? (this.currentFlow.category || NO_CATEGORY) : ''
    },
    currentFlowSubCategory() {
      return this.currentFlow ? (this.currentFlow.subCategory || '').trim() : ''
    },
    // 「申请标题」表单标签列宽（下限沿用原 90px，避免短表单被拉宽）
    titleLabelWidth() {
      return labelWidthOf(['申请标题'], { min: 90 })
    },
    // 「指定审批人」表单标签列宽：按最长的审批人节点名自适应，避免节点名折行
    selfLabelWidth() {
      const labels = this.selfNodes.map(n => (n.props && n.props.name) || '审批人')
      return labelWidthOf(labels, { min: 90 })
    }
  },
  watch: {
    // 分类列表变化（搜索/首屏加载）时，保证 activeCategory 始终有效
    flowGroups(val) {
      if (!val.length) { this.activeCategory = ''; return }
      if (!val.some(g => g.category === this.activeCategory)) this.activeCategory = val[0].category
    }
  },
  created() {
    this.loadFlows()
    this.loadOrg()
  },
  methods: {
    loadFlows() {
      this.flowLoading = true
      publishedFlows().then(res => {
        if (res && res.code === 200) this.flows = res.data || []
      }).finally(() => { this.flowLoading = false })
    },
    // 已选流程 → 图标与配色（优先自定义图标，其次按关键词映射）
    iconOf(flow) {
      return pickIcon(flow ? flow.category : '', flow ? flow.flowName : '').icon
    },
    iconStyle(flow) {
      if (flow && flow.iconUrl) return { background: 'transparent' }
      const hit = pickIcon(flow ? flow.category : '', flow ? flow.flowName : '')
      return { color: hit.color, background: hit.bg }
    },
    iconUrlOf(flow) {
      return fullIconUrl(flow && flow.iconUrl, this.$store.state.IPAss.TP)
    },
    // 重新选择：展开卡片区（此时 flowId 置空，下方表单区同步隐藏）
    reselect() {
      this.flowId = null
      this.flowKey = ''
      this.title = ''
      this.formSchema = null
      this.formData = {}
      this.selfNodes = []
      this.selfAssignees = {}
    },
    loadOrg() {
      getUserList({ page: 1, limit: 2000, isValid: 1 }).then(res => {
        if (res && res.code === 200 && res.data) this.userOptions = res.data.records || res.data || []
      })
      getDepartmentList().then(res => {
        if (res && res.code === 200) this.deptOptions = this.flattenDept(res.data || [])
      })
      getSaleChanceOptions().then(res => {
        if (res && res.code === 200) this.chanceOptions = res.data || []
      })
      getSaleChanceOptions({ scope: 'mine' }).then(res => {
        if (res && res.code === 200) this.myChanceOptions = res.data || []
      })
      getSaleProjectOptions().then(res => {
        if (res && res.code === 200) this.projectOptions = res.data || []
      })
      getSaleProjectOptions({ scope: 'mine' }).then(res => {
        if (res && res.code === 200) this.myProjectOptions = res.data || []
      })
      getTRoleSelectByPage({ page: 1, limit: 999, roleName: '' }).then(res => {
        if (res && res.code === 200) this.roleOptions = res.data || []
      }).catch(() => {})
    },
    // 扁平化部门树：保留 parentId（层级选择）与 isValid（停用过滤）；
    // 说明：仍保留停用节点，仅用于历史单据的部门名称回显，选择与成员过滤时排除
    flattenDept(tree) {
      const out = []
      const walk = (list, pid) => (list || []).forEach(n => {
        if (!n) return
        // 后端 DepartmentVO 已返回 parentId；缺失时回退用遍历时的父节点 id
        const parentId = n.parentId != null ? n.parentId : (pid != null ? pid : null)
        out.push({ id: n.id, name: n.name, parentId, isValid: n.isValid })
        walk(n.children, n.id)
      })
      walk(tree, null)
      return out
    },
    onFlowChange(id) {
      this.flowId = id
      const f = this.flows.find(x => x.id === id)
      this.flowKey = f ? f.flowKey : ''
      this.title = f ? this.buildDefaultTitle(f.flowName) : ''
      this.formData = {}
      this.formSchema = null
      this.selfNodes = []
      this.selfAssignees = {}
      if (!id) return
      // 加载流程定义(取 flowJson 找自选节点 + formId)
      getFlow(id).then(res => {
        if (res && res.code === 200 && res.data) {
          const d = res.data
          this.flowKey = d.flowKey
          // 解析自选审批人节点
          try {
            const root = JSON.parse(d.flowJson)
            this.selfNodes = flatten(root).filter(n =>
              (n.type === 'approval' || n.type === 'cc') && n.props && n.props.assigneeType === 'self')
            const sa = {}
            this.selfNodes.forEach(n => { sa[n.nodeKey] = [] })
            this.selfAssignees = sa
          } catch (e) { this.selfNodes = [] }
          // 加载绑定表单
          if (d.formId) {
            getForm(d.formId).then(fr => {
              if (fr && fr.code === 200 && fr.data) this.formSchema = fr.data.formJson || '{"fields":[]}'
            })
          } else {
            this.formSchema = '{"fields":[]}'
          }
        }
      })
    },
    // 默认申请标题：流程名称_登录用户真实名称_YYYYMMDD
    buildDefaultTitle(flowName) {
      const u = this.$store.state.currentUser || {}
      const d = new Date()
      const ymd = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0')
      return (flowName || '') + '_' + (u.trueName || u.userName || '') + '_' + ymd
    },
    resetAll() {
      this.formData = {}
      const sa = {}
      this.selfNodes.forEach(n => { sa[n.nodeKey] = [] })
      this.selfAssignees = sa
      if (this.$refs.formRenderer) this.$refs.formRenderer.clearValidate()
    },
    async submit() {
      if (!this.flowId) { this.$message.warning('请选择流程'); return }
      // 表单校验
      if (this.$refs.formRenderer) {
        const ok = await this.$refs.formRenderer.validate()
        if (!ok) { this.$message.warning('请完整填写表单'); return }
      }
      // 自选审批人校验
      for (const n of this.selfNodes) {
        if (!this.selfAssignees[n.nodeKey] || !this.selfAssignees[n.nodeKey].length) {
          this.$message.warning('请为「' + ((n.props && n.props.name) || '审批人') + '」选择审批人')
          return
        }
      }
      this.submitting = true
      const payload = {
        flowKey: this.flowKey,
        title: this.title,
        formData: this.formData,
        selfAssignees: this.selfAssignees
      }
      startProcess(payload).then(res => {
        this.submitting = false
        if (res && res.code === 200) {
          this.$message.success('发起成功')
          this.flowId = null
          this.resetAll()
          this.formSchema = null
          this.title = ''
        }
      }).catch(() => { this.submitting = false })
    }
  }
}
</script>

<style scoped>
.wf-apply { padding: 4px; }
.wf-apply-card { max-width: 1000px; }
.wf-apply-card >>> .el-card__body { padding: 20px 24px; }
.wf-apply-head { font-size: 16px; font-weight: 600; color: #303133; }

/* 选择流程 */
.wf-picker { margin-bottom: 8px; }
.wf-picker__search { width: 320px; margin-bottom: 16px; }
.wf-picker__tabs { display: flex; flex-wrap: wrap; gap: 28px; border-bottom: 1px solid #ebeef5; margin-bottom: 20px; }
.wf-tab { position: relative; padding: 0 2px 12px; font-size: 14px; color: #606266; cursor: pointer; transition: color .2s; }
.wf-tab:hover { color: #2f6bff; }
.wf-tab.is-active { color: #303133; font-weight: 600; }
.wf-tab.is-active::after { content: ''; position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; background: #2f6bff; border-radius: 2px; }

.wf-picker__cards { min-height: 64px; }
.wf-picker__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }
/* 二级分类分组之间：36px 总间距 */
.wf-picker__grid + .wf-picker__group, .wf-picker__group + .wf-picker__group { margin-top: 36px; }
.wf-sub-title { margin-bottom: 10px; padding-left: 8px; border-left: 3px solid #2f6bff; font-size: 13px; color: #909399; line-height: 1.2; }
.wf-flow-card { display: flex; align-items: center; padding: 12px 16px; background: #fff; border: 1px solid #ebeef5; border-radius: 8px; cursor: pointer; transition: all .2s; }
.wf-flow-card:hover { border-color: #b9cdff; box-shadow: 0 4px 12px rgba(47, 107, 255, .12); transform: translateY(-1px); }
.wf-flow-card__icon, .wf-picked__icon { flex: 0 0 32px; width: 32px; height: 32px; margin-right: 10px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 17px; }
.wf-flow-ico-img { width: 24px; height: 24px; object-fit: contain; }
.wf-flow-card__name { flex: 1; min-width: 0; font-size: 14px; color: #303133; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wf-picker__empty { padding: 24px 0; text-align: center; color: #909399; font-size: 13px; }

/* 已选流程（折叠态） */
.wf-picked { display: flex; align-items: center; padding: 10px 16px; background: #f7f9ff; border: 1px solid #dce6ff; border-radius: 8px; }
.wf-picked__name { font-size: 14px; font-weight: 600; color: #303133; }
.wf-picked__cat { margin-left: 10px; padding: 1px 8px; font-size: 12px; color: #909399; background: #fff; border: 1px solid #ebeef5; border-radius: 10px; }
.wf-picked .el-button { margin-left: auto; }

.wf-apply-form, .wf-apply-self { margin-top: 8px; }
.wf-apply-footer { margin-top: 16px; text-align: center; }
</style>
